var kind = require("enyo/kind");

/**
 * [ViewContainerSupport]{@link app.core.ViewContainerSupport} allows a control
 * to act as container for a view.
 *
 * The view won't be owned by the container and won't bubble events to the
 * container. The only task a container has is to waterfall resize events.
 *
 */
module.exports = {

    /**
     * Sets the current control as the container of the view. This will achieve
     * the following:
     *  - This control will become the view's `container`
     *  - This control will become the view's `parent` and as such events will
     *    waterfall to the view as well.
     *  - The view's owner remains unchanged.
     *
     * If the view has already got a node, it will be
     *
     * @param  {enyo.Control}  view  The view
     * @memberof app.core.ViewContainerSupport
     * @public
     */
    mountView: function(view) {
        var currentView = this.get("view");

        // We need to unregister the current view first
        if (currentView && view !== currentView) {
            this.unmountView(currentView);
        }

        // View is already mounted elsewhere, we need to unmount it first.
        if (view.isMounted) {
            view.container.unmountView(view);
        }

        // Optional beforeMount hook
        view.beforeMount && view.beforeMount();

        // Register the new view
        view.set("container", this);

        if (view.hasNode()) {
            view.addNodeToParent();
            view.refreshAbsoluteShowing();
        }
        else {
            view.render();
        }

        this.set("view", view);
        view.set("isMounted", true);

        // Optional afterMount hook
        view.afterMount && view.afterMount();

    },

    /**
     * Unregister the view.
     *
     * @param  {enyo.Control}  view  The view
     * @memberof app.core.ViewContainerSupport
     * @public
     */
    unmountView: function(view) {
        var currentView = this.get("view");

        if (view === currentView) {
            // Optional beforeUnmount hook
            view.beforeUnmount && view.beforeUnmount();

            view.setContainer(null);
            view.removeNodeFromDom();
            view.set("absoluteShowing", false);
            view.set("container", null);
            this.set("view", null);
            view.set("isMounted", false);

            // Optional afterUnmount hook
            view.afterUnmount && view.afterUnmount();
        }
    },

    /**
     * Unmount the view before we tear down the rendered DOM. Destruction of the
     * view's DOM content is not our job, because we cannot know if the intent
     * is to re-use it elsewhere.
     *
     * @private
     */
    beforeTeardown: kind.inherit(function(sup) {
        return function() {
            var view = this.view;

            view && this.unmountView(view);
            sup.apply(this, arguments);
        };
    }),

    /**
     * Unmount the view before we destroy ourselves. Destruction of the view is
     * not our job, because we cannot know if the intent is to re-use it
     * elsewhere.
     *
     * @private
     */
    destroy: kind.inherit(function(sup) {
        return function() {
            var view = this.view;

            view && this.unmountView(view);
            sup.apply(this, arguments);
        };
    })

};
